// Composant Modal pour la création/modification d'une commission
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiCalendar, FiUsers, FiLoader, FiFileText, FiHome } from 'react-icons/fi';
import { theme } from '@theme';
import { Commission } from '@/models/Commission';
import User from '@/models/User';
import { UserServices } from '@/services/UserServices';

// Types
interface CommissionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: Commission) => Promise<void>;
  initialData?: Partial<Commission>;
  selectedCandidatesCount: number;
  isLoading?: boolean;
}

// Styled Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(50px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text}60;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
  }

  svg {
    font-size: 24px;
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
  overflow-y: auto;
  flex: 1;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const RequiredIndicator = styled.span`
  color: ${theme.colors.error};
  margin-left: ${theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &:disabled {
    background: ${theme.colors.surface};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const InfoBox = styled.div`
  background: ${theme.colors.info}15;
  border: 1px solid ${theme.colors.info};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UserSelectionContainer = styled.div`
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  max-height: 200px;
  overflow-y: auto;
`;

const UserCheckboxItem = styled.label`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm};
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${theme.colors.primary};
  margin-right: ${theme.spacing.sm};
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const UserName = styled.span`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const UserEmail = styled.span`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const SelectedCount = styled.div`
  margin-top: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.primary}10;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.weights.semibold};
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.lg};
  color: ${theme.colors.text}60;
  gap: ${theme.spacing.sm};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}10;
  border: 1px solid ${theme.colors.error}40;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  color: ${theme.colors.error};
  font-size: ${theme.typography.sizes.sm};
  margin-bottom: ${theme.spacing.md};
`;

const ModalFooter = styled.div`
  padding: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.surface};
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;

    &:hover:not(:disabled) {
      background: ${theme.colors.primary}dd;
    }

    &:disabled {
      background: ${theme.colors.surface};
      color: ${theme.colors.text}40;
      cursor: not-allowed;
    }
  ` : `
    background: transparent;
    color: ${theme.colors.text}80;

    &:hover {
      background: ${theme.colors.surface};
    }
  `}

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export const CommissionFormModal: React.FC<CommissionFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  selectedCandidatesCount,
  isLoading = false
}) => {
  // États du formulaire
  const [formData, setFormData] = useState<Partial<Commission>>({
    title: '',
    scheduled_date: '',
    description: '',
    president: '',
    nombre_logements_disponibles: 0,
    notes: '',
    members: [],
    status: 'in_progress',
    ...initialData
  });

  // États pour les utilisateurs
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<number[]>(
    Array.isArray(initialData?.members) && initialData.members.length > 0 && typeof initialData.members[0] === 'number'
      ? (initialData.members as number[])
      : []
  );

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Charger les utilisateurs
  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      setUsersError(null);
      const response = await UserServices.getUsers('') as { success: boolean; data?: User[] };

      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        setUsersError('Impossible de charger la liste des utilisateurs');
      }
    } catch (error: any) {
      console.error('Erreur chargement utilisateurs:', error);
      setUsersError('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Gestion des changements de formulaire
  const handleChange = (field: keyof Commission, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Effacer l'erreur pour ce champ
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Gestion de la sélection des membres
  const toggleMember = (userId: number) => {
    setSelectedMembers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Validation du formulaire
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.scheduled_date) {
      newErrors.scheduled_date = 'La date et l\'heure sont requises';
    }

    if (!formData.nombre_logements_disponibles || formData.nombre_logements_disponibles <= 0) {
      newErrors.nombre_logements_disponibles = 'Le nombre de logements doit être supérieur à 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Préparer les données avec les membres sélectionnés
    const submissionData: Commission = {
      ...formData,
      members: selectedMembers,
      title: formData.title || '',
      scheduled_date: formData.scheduled_date || '',
      status: formData.status || 'scheduled'
    } as Commission;

    await onSubmit(submissionData);
  };

  // Réinitialiser le formulaire à la fermeture
  const handleClose = () => {
    setFormData({
      title: '',
      scheduled_date: '',
      start_time: '',
      end_time: '',
      description: '',
      president: '',
      nombre_logements_disponibles: 0,
      notes: '',
      members: [],
      status: 'scheduled'
    });
    setSelectedMembers([]);
    setErrors({});
    onClose();
  };

  // Extraire la date et l'heure du scheduled_date
  const getDateValue = (field: keyof Commission) => {
    const value = formData[field];
    if (!value || typeof value !== 'string') return '';
    return value.split('T')[0];
  };

  const getTimeValue = (field: keyof Commission) => {
    const value = formData[field];
    if (!value || typeof value !== 'string') return '';
    const timePart = value.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  };

  const handleDateChange = (date: string) => {
    const time = getTimeValue('start_time') || '09:00';
    handleChange('start_time', `${date}T${time}:00`);
  };

  const handleTimeChange = (time: string) => {
    const date = getDateValue('start_time');
    if (date) {
      handleChange('start_time', `${date}T${time}:00`);
    }
  };

  const handleDateEndChange = (date: string) => {
    const time = getTimeValue('end_time') || '09:00';
    handleChange('end_time', `${date}T${time}:00`);
  };

  const handleTimeEndChange = (time: string) => {
    const date = getDateValue('end_time');
    if (date) {
      handleChange('end_time', `${date}T${time}:00`);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FiCalendar />
            Planifier une Commission d'Attribution
          </ModalTitle>
          <CloseButton onClick={handleClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <InfoBox>
            <FiUsers />
            <div>
              Vous allez créer une commission en ligne avec <strong>{selectedCandidatesCount} candidat(s)</strong> sélectionné(s).
            </div>
          </InfoBox>

          {/* Titre */}
          <FormGroup>
            <Label>
              Titre de la commission
              <RequiredIndicator>*</RequiredIndicator>
            </Label>
            <Input
              type="text"
              value={formData.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Ex: Commission d'Attribution - T3 Cocody"
            />
            {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
          </FormGroup>

          {/* Date et Heure */}
          <FormRow>
            <FormGroup>
              <Label>
                Date de debut:
                <RequiredIndicator>*</RequiredIndicator>
              </Label>
              <Input
                type="date"
                value={getDateValue('start_time')}
                onChange={(e) => handleDateChange(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.scheduled_date && <ErrorMessage>{errors.scheduled_date}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>
                Heure
                <RequiredIndicator>*</RequiredIndicator>
              </Label>
              <Input
                type="time"
                value={getTimeValue('start_time')}
                onChange={(e) => handleTimeChange(e.target.value)}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>
                Date de fin
                <RequiredIndicator>*</RequiredIndicator>
              </Label>
              <Input
                type="date"
                value={getDateValue('end_time')}
                onChange={(e) => handleDateEndChange(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.scheduled_date && <ErrorMessage>{errors.scheduled_date}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>
                Heure
                <RequiredIndicator>*</RequiredIndicator>
              </Label>
              <Input
                type="time"
                value={getTimeValue('end_time')}
                onChange={(e) => handleTimeEndChange(e.target.value)}
              />
            </FormGroup>
          </FormRow>

          {/* Description */}
          <FormGroup>
            <Label>Description</Label>
            <TextArea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Description de la commission (optionnel)"
            />
          </FormGroup>

          {/* Président */}
          <FormGroup>
            <Label>Président de la commission</Label>
            <Input
              type="text"
              value={formData.president || ''}
              onChange={(e) => handleChange('president', e.target.value)}
              placeholder="Nom du président (optionnel)"
            />
          </FormGroup>

          {/* Nombre de logements */}
          <FormGroup>
            <Label>
              Nombre de logements disponibles
              <RequiredIndicator>*</RequiredIndicator>
            </Label>
            <Input
              type="number"
              min="1"
              value={formData.nombre_logements_disponibles || ''}
              onChange={(e) => handleChange('nombre_logements_disponibles', parseInt(e.target.value) || 0)}
              placeholder="Ex: 10"
            />
            {errors.nombre_logements_disponibles && (
              <ErrorMessage>{errors.nombre_logements_disponibles}</ErrorMessage>
            )}
          </FormGroup>

          {/* Sélection des membres */}
          <FormGroup>
            <Label>
              <FiUsers /> Membres de la commission
            </Label>
            {loadingUsers ? (
              <LoadingSpinner>
                <FiLoader className="spinner" />
                Chargement des utilisateurs...
              </LoadingSpinner>
            ) : usersError ? (
              <ErrorMessage>{usersError}</ErrorMessage>
            ) : (
              <>
                <UserSelectionContainer>
                  {users.length === 0 ? (
                    <div style={{ padding: theme.spacing.md, textAlign: 'center', color: theme.colors.text + '60' }}>
                      Aucun utilisateur disponible
                    </div>
                  ) : (
                    users.map(user => (
                      <UserCheckboxItem key={user.id}>
                        <Checkbox
                          type="checkbox"
                          checked={selectedMembers.includes(user.id)}
                          onChange={() => toggleMember(user.id)}
                        />
                        <UserInfo>
                          <UserName>{user.first_name} {user.last_name}</UserName>
                          <UserEmail>{user.email}</UserEmail>
                        </UserInfo>
                      </UserCheckboxItem>
                    ))
                  )}
                </UserSelectionContainer>
                {selectedMembers.length > 0 && (
                  <SelectedCount>
                    {selectedMembers.length} membre(s) sélectionné(s)
                  </SelectedCount>
                )}
              </>
            )}
          </FormGroup>

          {/* Notes */}
          <FormGroup>
            <Label>Notes et observations</Label>
            <TextArea
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Notes complémentaires (optionnel)"
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FiLoader className="spinner" />
                Création...
              </>
            ) : (
              <>
                <FiCalendar />
                Créer la Commission
              </>
            )}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};
